/**
 * Image dimensions from headers, without decoding (§33).
 *
 * `isPixelBudgetExceeded()` in the security policy needs width and height
 * *before* anything allocates a bitmap — that is the whole point of the check.
 * A 40000×40000 PNG is a few megabytes on disk and 6.4 GB decoded, so asking a
 * decoder for the dimensions defeats the guard it is meant to feed.
 *
 * Every reader here touches only the header bytes: tens to a few hundred, never
 * the pixel data. That also makes it safe to run on files we are refusing —
 * we learn how big a hostile image claims to be without honouring the claim.
 */

export interface ImageDimensions {
  readonly width: number;
  readonly height: number;
  /** Frames or sub-images, when the container declares more than one. */
  readonly frameCount: number | undefined;
  /** Bits per channel where cheaply available; feeds the memory estimate. */
  readonly bitDepth: number | undefined;
  readonly hasAlpha: boolean | undefined;
}

export type ImageProbeResult =
  | { readonly ok: true; readonly format: string; readonly dimensions: ImageDimensions }
  | { readonly ok: false; readonly reason: ImageProbeFailure };

export type ImageProbeFailure =
  | 'unrecognised'
  | 'truncated'
  | 'malformed'
  | 'dimensions-not-declared';

const fail = (reason: ImageProbeFailure): ImageProbeResult => ({ ok: false, reason });

const ok = (
  format: string, width: number, height: number,
  extra: Partial<ImageDimensions> = {},
): ImageProbeResult => ({
  ok: true,
  format,
  dimensions: {
    width, height,
    frameCount: extra.frameCount,
    bitDepth: extra.bitDepth,
    hasAlpha: extra.hasAlpha,
  },
});

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function startsWith(bytes: Uint8Array, sig: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + sig.length) return false;
  for (let i = 0; i < sig.length; i++) if (bytes[offset + i] !== sig[i]) return false;
  return true;
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  let s = '';
  for (let i = 0; i < length; i++) s += String.fromCharCode(bytes[offset + i] ?? 0);
  return s;
}

/* ------------------------------------------------------------------ *
 * PNG
 * ------------------------------------------------------------------ */

const PNG_COLOUR_ALPHA = new Set([4, 6]); // greyscale+alpha, truecolour+alpha

function probePng(bytes: Uint8Array): ImageProbeResult {
  // 8-byte signature, then a 4-byte length and the "IHDR" type: dimensions
  // start at 16 and are big-endian.
  if (bytes.length < 33) return fail('truncated');
  if (ascii(bytes, 12, 4) !== 'IHDR') return fail('malformed');
  const v = view(bytes);
  const width = v.getUint32(16, false);
  const height = v.getUint32(20, false);
  const bitDepth = bytes[24];
  const colourType = bytes[25] ?? 0;
  if (width === 0 || height === 0) return fail('malformed');
  return ok('png', width, height, {
    bitDepth,
    hasAlpha: PNG_COLOUR_ALPHA.has(colourType),
  });
}

/* ------------------------------------------------------------------ *
 * JPEG
 * ------------------------------------------------------------------ */

/**
 * Frame markers that carry dimensions. SOF4 (0xC4, DHT), SOF8 (0xC8, reserved)
 * and SOF12 (0xCC, DAC) share the numeric range but are not frame headers, so
 * they are excluded — reading them as SOF yields nonsense sizes.
 */
const JPEG_SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7,
  0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

function probeJpeg(bytes: Uint8Array): ImageProbeResult {
  const v = view(bytes);
  let i = 2; // skip SOI
  while (i + 3 < bytes.length) {
    if (bytes[i] !== 0xff) { i++; continue; } // resync past padding
    const marker = bytes[i + 1]!;
    if (marker === 0xff) { i++; continue; } // fill byte
    // Standalone markers carry no length payload.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) { i += 2; continue; }

    if (i + 3 >= bytes.length) return fail('truncated');
    const segmentLength = v.getUint16(i + 2, false);
    if (segmentLength < 2) return fail('malformed');

    if (JPEG_SOF_MARKERS.has(marker)) {
      if (i + 9 > bytes.length) return fail('truncated');
      const precision = bytes[i + 4];
      const height = v.getUint16(i + 5, false);
      const width = v.getUint16(i + 7, false);
      const components = bytes[i + 9];
      if (width === 0 || height === 0) return fail('malformed');
      return ok('jpeg', width, height, {
        bitDepth: precision,
        hasAlpha: false, // baseline JPEG has no alpha channel
        frameCount: components === undefined ? undefined : 1,
      });
    }

    if (marker === 0xda) break; // start of scan — no frame header found
    i += 2 + segmentLength;
  }
  return fail('dimensions-not-declared');
}

/* ------------------------------------------------------------------ *
 * GIF
 * ------------------------------------------------------------------ */

function probeGif(bytes: Uint8Array): ImageProbeResult {
  if (bytes.length < 10) return fail('truncated');
  const v = view(bytes);
  const width = v.getUint16(6, true);
  const height = v.getUint16(8, true);
  if (width === 0 || height === 0) return fail('malformed');
  // Counting frames means walking every block, which costs more than the
  // budget check needs; the flag that matters is left undefined rather than
  // guessed.
  return ok('gif', width, height, { bitDepth: 8, hasAlpha: true });
}

/* ------------------------------------------------------------------ *
 * WebP
 * ------------------------------------------------------------------ */

function probeWebp(bytes: Uint8Array): ImageProbeResult {
  if (bytes.length < 30) return fail('truncated');
  const v = view(bytes);
  const chunk = ascii(bytes, 12, 4);

  if (chunk === 'VP8 ') {
    // Lossy: a 3-byte start code, then 14-bit width and height.
    if (!startsWith(bytes, [0x9d, 0x01, 0x2a], 23)) return fail('malformed');
    const width = v.getUint16(26, true) & 0x3fff;
    const height = v.getUint16(28, true) & 0x3fff;
    return ok('webp', width, height, { hasAlpha: false });
  }

  if (chunk === 'VP8L') {
    // Lossless: signature byte 0x2F, then 14 bits of (width-1) and (height-1)
    // packed little-endian across the next four bytes.
    if (bytes[20] !== 0x2f) return fail('malformed');
    const bits = v.getUint32(21, true);
    const width = (bits & 0x3fff) + 1;
    const height = ((bits >>> 14) & 0x3fff) + 1;
    const hasAlpha = ((bits >>> 28) & 0x1) === 1;
    return ok('webp', width, height, { hasAlpha });
  }

  if (chunk === 'VP8X') {
    // Extended: 24-bit canvas width-1 and height-1.
    const width = (bytes[24]! | (bytes[25]! << 8) | (bytes[26]! << 16)) + 1;
    const height = (bytes[27]! | (bytes[28]! << 8) | (bytes[29]! << 16)) + 1;
    const flags = bytes[20] ?? 0;
    return ok('webp', width, height, {
      hasAlpha: (flags & 0x10) !== 0,
      frameCount: (flags & 0x02) !== 0 ? undefined : 1, // animation flag
    });
  }

  return fail('malformed');
}

/* ------------------------------------------------------------------ *
 * BMP
 * ------------------------------------------------------------------ */

function probeBmp(bytes: Uint8Array): ImageProbeResult {
  if (bytes.length < 26) return fail('truncated');
  const v = view(bytes);
  const dibSize = v.getUint32(14, true);

  // BITMAPCOREHEADER uses 16-bit dimensions; everything since uses signed 32-bit.
  if (dibSize === 12) {
    const width = v.getUint16(18, true);
    const height = v.getUint16(20, true);
    return ok('bmp', width, height, { bitDepth: v.getUint16(24, true) });
  }
  if (dibSize < 40 || bytes.length < 30) return fail('malformed');
  const width = v.getInt32(18, true);
  // A negative height means the rows are stored top-down; the magnitude is the
  // real height, and treating the sign as part of the number would make the
  // pixel-budget check compute a negative area and pass a huge image.
  const height = Math.abs(v.getInt32(22, true));
  const bitDepth = v.getUint16(28, true);
  if (width <= 0 || height === 0) return fail('malformed');
  return ok('bmp', width, height, { bitDepth, hasAlpha: bitDepth === 32 });
}

/* ------------------------------------------------------------------ *
 * ICO / CUR
 * ------------------------------------------------------------------ */

function probeIco(bytes: Uint8Array): ImageProbeResult {
  if (bytes.length < 22) return fail('truncated');
  const v = view(bytes);
  const count = v.getUint16(4, true);
  if (count === 0) return fail('malformed');

  // Report the largest entry: that is what a viewer displays, and what the
  // memory budget must be computed against.
  let width = 0;
  let height = 0;
  for (let i = 0; i < count; i++) {
    const entry = 6 + i * 16;
    if (entry + 16 > bytes.length) break;
    // A stored 0 means 256 — the field is a single byte.
    const w = bytes[entry] === 0 ? 256 : bytes[entry]!;
    const h = bytes[entry + 1] === 0 ? 256 : bytes[entry + 1]!;
    if (w * h > width * height) { width = w; height = h; }
  }
  if (width === 0) return fail('malformed');
  return ok('ico', width, height, { frameCount: count });
}

/* ------------------------------------------------------------------ *
 * TIFF
 * ------------------------------------------------------------------ */

const TIFF_TAG_WIDTH = 256;
const TIFF_TAG_HEIGHT = 257;
const TIFF_TAG_BITS_PER_SAMPLE = 258;

function readTiffValue(v: DataView, offset: number, type: number, little: boolean): number | null {
  switch (type) {
    case 1: return v.getUint8(offset);            // BYTE
    case 3: return v.getUint16(offset, little);   // SHORT
    case 4: return v.getUint32(offset, little);   // LONG
    default: return null;
  }
}

function probeTiff(bytes: Uint8Array): ImageProbeResult {
  if (bytes.length < 8) return fail('truncated');
  const little = bytes[0] === 0x49;
  const v = view(bytes);
  const ifdOffset = v.getUint32(4, little);
  if (ifdOffset + 2 > bytes.length) return fail('truncated');

  const entryCount = v.getUint16(ifdOffset, little);
  let width: number | null = null;
  let height: number | null = null;
  let bitDepth: number | null = null;

  for (let i = 0; i < entryCount; i++) {
    const entry = ifdOffset + 2 + i * 12;
    if (entry + 12 > bytes.length) break;
    const tag = v.getUint16(entry, little);
    const type = v.getUint16(entry + 2, little);
    // The value sits inline when it fits in four bytes, which it always does
    // for these tags.
    const value = readTiffValue(v, entry + 8, type, little);
    if (tag === TIFF_TAG_WIDTH) width = value;
    else if (tag === TIFF_TAG_HEIGHT) height = value;
    else if (tag === TIFF_TAG_BITS_PER_SAMPLE) bitDepth = value;
  }

  if (width === null || height === null) return fail('dimensions-not-declared');
  if (width === 0 || height === 0) return fail('malformed');
  return ok('tiff', width, height, { bitDepth: bitDepth ?? undefined });
}

/* ------------------------------------------------------------------ *
 * ISO base media (HEIC, AVIF)
 * ------------------------------------------------------------------ */

/**
 * Walk ISOBMFF boxes looking for `ispe` (image spatial extents).
 *
 * Depth-limited and length-validated: a malformed file can otherwise describe
 * a box that contains itself, and the walk becomes an infinite loop inside the
 * guard that is supposed to protect against hostile images.
 */
function findIspe(bytes: Uint8Array, start: number, end: number, depth: number): ImageProbeResult | null {
  if (depth > 8) return null;
  const v = view(bytes);
  let offset = start;

  while (offset + 8 <= end) {
    let size = v.getUint32(offset, false);
    const type = ascii(bytes, offset + 4, 4);
    let headerSize = 8;

    if (size === 1) {
      // 64-bit size. Anything beyond 2^32 is far past any sane image file, so
      // the high word is required to be zero rather than supported.
      if (offset + 16 > end) return null;
      if (v.getUint32(offset + 8, false) !== 0) return null;
      size = v.getUint32(offset + 12, false);
      headerSize = 16;
    } else if (size === 0) {
      size = end - offset; // box extends to the end
    }

    if (size < headerSize || offset + size > end) return null;

    if (type === 'ispe') {
      // FullBox: 1 version byte + 3 flag bytes, then two 32-bit extents.
      const p = offset + headerSize + 4;
      if (p + 8 > end) return null;
      const width = v.getUint32(p, false);
      const height = v.getUint32(p + 4, false);
      if (width === 0 || height === 0) return fail('malformed');
      return ok('isobmff', width, height, {});
    }

    // Containers that can hold ispe. Descending only into these avoids
    // interpreting media payloads as box structure.
    if (type === 'meta') {
      // meta is a FullBox: skip its 4 version/flags bytes before recursing.
      const found = findIspe(bytes, offset + headerSize + 4, offset + size, depth + 1);
      if (found) return found;
    } else if (type === 'iprp' || type === 'ipco' || type === 'moov' || type === 'trak' || type === 'mdia') {
      const found = findIspe(bytes, offset + headerSize, offset + size, depth + 1);
      if (found) return found;
    }

    offset += size;
  }
  return null;
}

function probeIsobmff(bytes: Uint8Array): ImageProbeResult {
  return findIspe(bytes, 0, bytes.length, 0) ?? fail('dimensions-not-declared');
}

/* ------------------------------------------------------------------ *
 * dispatch
 * ------------------------------------------------------------------ */

/** Bytes needed for a reliable probe of any supported format. */
export const IMAGE_PROBE_BYTES = 64 * 1024;

/**
 * Read image dimensions from header bytes.
 *
 * @param bytes The file, or at least its first {@link IMAGE_PROBE_BYTES}.
 *   TIFF and ISOBMFF may place their metadata late in the file; those return
 *   `dimensions-not-declared` rather than a wrong answer when given too little.
 */
export function probeImage(bytes: Uint8Array): ImageProbeResult {
  if (bytes.length < 12) return fail('truncated');

  if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return probePng(bytes);
  if (startsWith(bytes, [0xff, 0xd8, 0xff])) return probeJpeg(bytes);
  if (ascii(bytes, 0, 3) === 'GIF') return probeGif(bytes);
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') return probeWebp(bytes);
  if (startsWith(bytes, [0x42, 0x4d])) return probeBmp(bytes);
  if (startsWith(bytes, [0x00, 0x00, 0x01, 0x00]) || startsWith(bytes, [0x00, 0x00, 0x02, 0x00])) {
    return probeIco(bytes);
  }
  if (startsWith(bytes, [0x49, 0x49, 0x2a, 0x00]) || startsWith(bytes, [0x4d, 0x4d, 0x00, 0x2a])) {
    return probeTiff(bytes);
  }
  if (ascii(bytes, 4, 4) === 'ftyp') return probeIsobmff(bytes);

  return fail('unrecognised');
}

/**
 * Bytes a decoded bitmap will occupy.
 *
 * Deliberately assumes 4 bytes per pixel regardless of the declared bit depth:
 * every decoder in the stack expands to RGBA, so estimating from the source
 * depth would under-report by 4× for a 1-bit image and let it through the
 * budget it was supposed to fail.
 */
export function estimateDecodedBytes(d: ImageDimensions): number {
  return d.width * d.height * 4 * Math.max(1, d.frameCount ?? 1);
}
